import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { CheckCircle2, ExternalLink, Hash, Loader2, Printer, QrCode, Search, Send, XCircle } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { api, dt, errMsg, money } from "@/lib/api";
import { DeliveryBadge, Empty, ErrorBox, PageHeader, PayBadge, SaleStatusBadge } from "@/components/common";
import { SelectionPhotos } from "@/components/Selection";
import QrScanner from "@/components/QrScanner";

const STATUS = { valid: ["صالح", "bg-emerald-50 text-emerald-800 border-emerald-400", CheckCircle2], cancelled: ["ملغى", "bg-rose-50 text-rose-800 border-rose-400", XCircle],
  not_found: ["غير موجود", "bg-slate-100 text-slate-700 border-slate-400", XCircle] };

function ReceiptResult({ r }) {
  const nav = useNavigate();
  const [label, cls, Icon] = STATUS[r.status];
  const s = r.sale;
  return (
    <div className="k-card p-5 space-y-3 fade-up" data-testid="receipt-result">
      <div className={`rounded-xl border-2 p-3 flex items-center gap-2 font-extrabold text-lg ${cls}`} data-testid="receipt-status"><Icon className="w-6 h-6" /> الوصل: {label}</div>
      {s && <>
        <dl className="grid grid-cols-2 gap-y-2 text-sm">
          <dt className="text-slate-500">رقم الوصل</dt><dd className="font-bold num" data-testid="receipt-number">{r.payment?.number || s.number}{r.payment && <span className="text-xs text-slate-500"> (بيع {s.number})</span>}</dd>
          <dt className="text-slate-500">تاريخ البيع</dt><dd>{dt(s.created_at)}</dd>
          <dt className="text-slate-500">الزبون</dt><dd className="font-bold">{s.customer_name} <span className="num text-xs">{s.customer_phone}</span></dd>
          <dt className="text-slate-500">رقم الكبش</dt><dd className="font-black num text-xl text-[#1E40AF]">{s.ear_tag}</dd>
          {r.financial && <>
            <dt className="text-slate-500">السعر</dt><dd className="num">{money(s.total)}</dd>
            <dt className="text-slate-500">مجموع المدفوعات</dt><dd className="num text-emerald-700 font-bold" data-testid="receipt-paid">{money(s.paid_total)}</dd>
            <dt className="text-slate-500">المتبقي</dt><dd className="num text-rose-700 font-bold" data-testid="receipt-remaining">{money(s.remaining)}</dd>
            {r.payment && <><dt className="text-slate-500">مبلغ هذا الوصل</dt><dd className="num">{money(r.payment.amount)} – {r.payment.method}</dd></>}
          </>}
          <dt className="text-slate-500">حالة المعاملة</dt><dd className="flex flex-wrap gap-1"><SaleStatusBadge s={s.status} />{r.financial && <PayBadge s={s.payment_status} />}<DeliveryBadge s={s.delivery_status} /></dd>
        </dl>
        {r.financial && <div className="flex flex-wrap gap-2 pt-1">
          <button className="k-btn-primary w-full h-12 text-base" onClick={() => nav(`/sales/${s.id}`)} data-testid="receipt-open"><ExternalLink className="w-5 h-5" /> الدخول إلى المعاملة</button>
          <button className="k-btn-outline k-btn-sm" onClick={() => nav(r.payment ? `/print/payment/${r.payment.id}` : `/print/sale/${s.id}`)} data-testid="receipt-print"><Printer className="w-4 h-4" /> طباعة الوصل</button>
          <button className="k-btn-outline k-btn-sm" onClick={() => nav(`${r.payment ? `/print/payment/${r.payment.id}` : `/print/sale/${s.id}`}?send=1`)} data-testid="receipt-send"><Send className="w-4 h-4" /> إرسال الوصل</button>
        </div>}
        {r.permit && <div className="text-sm rounded-xl bg-[#F6F1E7] p-3" data-testid="receipt-permit">تصريح الخروج {r.permit.number}: <b>{{ valid: "صالح", used: "مستعمل (خرج)", cancelled: "ملغى", expired: "منتهي" }[r.permit.status] || r.permit.status}</b>
          {r.permit.delivered_at && ` • سُلّم ${dt(r.permit.delivered_at)}`}{r.permit.exited_at && ` • خرج ${dt(r.permit.exited_at)}`}</div>}
        <p className="text-xs text-slate-500">عرض فقط – المسح لا يعدّل المعاملة ولا يسجل خروج الكبش.</p>
      </>}
    </div>
  );
}

function ReceiptSearch({ initial }) {
  const [code, setCode] = useState("");
  const [res, setRes] = useState(null);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const go = async (c) => {
    if (!c?.trim()) return;
    setBusy(true); setErr(""); setRes(null);
    try { setRes((await api.get("/receipts/resolve", { params: { code: c.trim() } })).data); } catch (e) { setErr(errMsg(e)); } finally { setBusy(false); }
  };
  useEffect(() => { if (initial) go(initial); }, [initial]); // eslint-disable-line react-hooks/exhaustive-deps
  return (
    <div className="space-y-4">
      <div className="k-card p-5 space-y-3">
        <QrScanner onScan={(t) => go(t)} label="مسح QR Code" manualHint="أو ابحث يدويًا برقم الوصل" />
        <form onSubmit={(e) => { e.preventDefault(); go(code); }} className="flex gap-2">
          <input className="k-input h-12 num text-center" placeholder="رقم الوصل مثل S-2026-0001 أو P-2026-0001" value={code} onChange={(e) => setCode(e.target.value)} data-testid="receipt-code-input" />
          <button className="k-btn-primary h-12" disabled={busy || !code.trim()} data-testid="receipt-search-button">{busy ? <Loader2 className="w-5 h-5 animate-spin" /> : <Search className="w-5 h-5" />} بحث</button>
        </form>
      </div>
      <ErrorBox text={err} />
      {res && <ReceiptResult r={res} />}
    </div>
  );
}

function SheepSearch() {
  const [tag, setTag] = useState("");
  const [rows, setRows] = useState(null);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const go = async (e) => {
    e.preventDefault(); setErr(""); setBusy(true);
    try { const { data } = await api.get("/sales/lookup", { params: { tag } }); setRows(data); } catch (ex) { setErr(errMsg(ex)); } finally { setBusy(false); }
  };
  return (
    <div className="space-y-5">
      <form onSubmit={go} className="k-card p-5 flex gap-2">
        <input className="k-input h-14 text-2xl num text-center" placeholder="رقم الكبش" value={tag} onChange={(e) => setTag(e.target.value)} data-testid="lookup-tag-input" />
        <button className="k-btn-primary h-14 px-6" disabled={busy || !tag.trim()} data-testid="lookup-search-button">{busy ? <Loader2 className="w-5 h-5 animate-spin" /> : <Search className="w-5 h-5" />} بحث</button>
      </form>
      <ErrorBox text={err} />
      {rows && !rows.length && <Empty text="لا يوجد كبش مسجل بهذا الرقم" />}
      {rows?.map((r) => (
        <div key={r.id} className="k-card p-5 space-y-3 fade-up" data-testid={`lookup-result-${r.number}`}>
          <div className="flex flex-wrap items-end justify-between gap-3">
            <div><div className="text-xs text-slate-500">رقم الكبش</div><div className="text-5xl font-black num text-[#1E40AF]">{r.ear_tag}</div></div>
            <div className="flex flex-wrap gap-2"><SaleStatusBadge s={r.status} /><PayBadge s={r.payment_status} /><DeliveryBadge s={r.delivery_status} /></div>
          </div>
          <dl className="grid grid-cols-2 gap-y-2 text-sm">
            <dt className="text-slate-500">الزبون</dt><dd className="font-bold text-lg" data-testid="lookup-customer-name">{r.customer_name}</dd>
            <dt className="text-slate-500">الهاتف</dt><dd className="num">{r.customer_phone}</dd>
            <dt className="text-slate-500">رقم البيع</dt><dd className="num">{r.number} · موسم {r.season}</dd>
            <dt className="text-slate-500">القسم</dt><dd>{r.section_name}{r.category ? ` · ${r.category}` : ""}</dd>
            <dt className="text-slate-500">العامل</dt><dd>{r.worker_name}</dd>
            <dt className="text-slate-500">تاريخ البيع</dt><dd>{dt(r.created_at)}</dd>
          </dl>
          {r.selection_id && <div data-testid={`lookup-photos-${r.number}`}><div className="text-xs text-slate-500 mb-1">صور الكبش</div><SelectionPhotos sel={{ id: r.selection_id, photos: r.photos }} /></div>}
        </div>
      ))}
    </div>
  );
}

export default function Lookup() {
  const { token } = useParams();
  const { can } = useAuth();
  const [tab, setTab] = useState(token || !can("lookup_sheep") ? "receipt" : "sheep");
  const canSheep = can("lookup_sheep");
  const t = (k, label, Icon) => <button onClick={() => setTab(k)} data-testid={`lookup-tab-${k}`} className={`flex-1 h-12 rounded-xl font-bold flex items-center justify-center gap-2 ${tab === k ? "bg-[#1A4D2E] text-white" : "bg-white border"}`}><Icon className="w-5 h-5" /> {label}</button>;
  return (
    <div className="max-w-3xl mx-auto space-y-5">
      <PageHeader title="البحث" subtitle="ابحث برقم الكبش، أو امسح QR الوصل / أدخل رقمه" />
      <div className="flex gap-2">{canSheep && t("sheep", "رقم الكبش", Hash)}{t("receipt", "وصل / QR Code", QrCode)}</div>
      {tab === "sheep" ? <SheepSearch /> : <ReceiptSearch initial={token} />}
    </div>
  );
}
